import React, { useState, useEffect, useRef } from 'react';
import './Beginnings.css';

const Beginnings = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const containerRef = useRef(null);
  const timelineRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Timeline milestones data
  const milestones = [
    {
      year: '1994',
      month: 'Born',
      title: 'Software in My DNA',
      subtitle: 'The First Step of a Technological Journey',
      description: 'As the son of Austrian software pioneer Klaus Propst, who significantly shaped the medical software industry, Julian grows up in an entrepreneurial family where technology and innovation are part of everyday life. <strong>Already during school, he develops his own tools for fun – the beginning of a journey that combines creative ideas with technical precision.</strong>',
      images: [
        '/img/history_julian_1.jpg',
        '/img/julian.png'
      ],
      logo: null
    },
    {
      year: '2020',
      month: 'Vienna',
      title: 'DemoCharts',
      titleNote: '(now Aaartists.com)',
      subtitle: 'AWS First Investment Winner – Building Communities at Scale',
      description: 'With DemoCharts, Julian\'s team wins the prestigious AWS First Investment grant in Vienna. The platform enables musicians to present their songs digitally and get discovered. <strong>With over 10,000 registered artists, DemoCharts becomes one of Austria\'s most exciting music startups – demonstrating Julian\'s instinct for community building and scalable products.</strong> The platform was later acquired by Aaartists.com.',
      images: [
        '/img/history_julian_2.jpg',
        '/img/julian.png'
      ],
      logo: '/img/Democharts_Logo_White.png',
      url: 'https://aaartists.com'
    },
    {
      year: '2021',
      month: 'Launch',
      titleNote: 'WavyMagazine.com',
      title: 'Wavy Magazine',
      subtitle: 'Thousands of Artists, One Stage – Brands as Growth Platforms',
      description: 'With the online music magazine WavyMagazine.com, Julian creates a stage for emerging artists. To date, the magazine has helped thousands of musicians expand their reach and build credibility. <strong>A project that showcases how Julian thinks about brands – as platforms for growth, exchange, and inspiration.</strong>',
      images: [
        '/img/history_julian_3.jpg',
        '/img/julian.png'
      ],
      logo: '/img/wavy_magazine_tablet_logo.png',
      logoInvert: true,
      url: 'https://wavymagazine.com'
    },
    {
      year: '2022',
      month: 'Scale',
      title: 'BonusClub',
      titleNote: 'BonusClub.io',
      subtitle: 'Reputation & Revenue for Hospitality – Tech meets Hospitality',
      description: 'With BonusClub, Julian transfers his principles of reach and reputation to the hospitality industry. The platform helps hotels and Airbnb hosts achieve more positive reviews, better guest relationships, and higher occupancy rates. <strong>BonusClub becomes a prime example of how technology elevates hospitality to a new level.</strong>',
      images: [
        '/img/julian_5.jpg',
        '/img/bc_1.jpg'
      ],
      logo: '/img/bonusclub_logo.webp',
      logoInvert: true,
      url: 'https://bonusclub.io'
    },
    {
      year: '2024',
      month: 'Innovation',
      title: 'BonusClub AI',
      subtitle: 'AI-Powered Growth – Logical. Strategic. Measurable.',
      description: 'An internal experiment evolves into BonusClub AI – an AI-based multi-touchpoint engine for B2B companies. It unites branding, positioning, and automated sales in a single system. <strong>BonusClub AI represents Julian\'s vision: growth that happens not loudly, but logically, strategically, and measurably.</strong>',
      images: [
        '/img/julian_12.jpg',
        '/img/julian_12.jpg'
      ],
      logo: '/img/salesgod_logo_hell.webp',
      logoInvert: true,
      url: 'https://bonusclub.io/de'
    },
    {
      year: '2025',
      month: 'Vision',
      title: 'The Vision',
      subtitle: 'AI Systems That Turn Visionaries Into Market Leaders',
      description: 'We develop and scale intelligent AI systems for companies that want to redefine their industry. Julian invests where innovation meets execution power – in technologies that don\'t just impress, but perform.',
      images: [
        '/img/julian_11.jpg',
        '/img/julian_11.jpg'
      ],
      logo: null
    }
  ];

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll animation handler
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (!containerRef.current) {
            ticking = false;
            return;
          }

          const container = containerRef.current;
          const rect = container.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          
          // Calculate scroll progress (0 to 1)
          const scrollStart = rect.top - windowHeight;
          const scrollEnd = rect.bottom;
          const scrollDistance = scrollEnd - scrollStart;
          const currentScroll = -scrollStart;
          const progress = Math.max(0, Math.min(1, currentScroll / scrollDistance));
          
          setScrollProgress(progress);

          // Update active milestone based on scroll progress
          const newActiveIndex = Math.floor(progress * (milestones.length - 1));
          setActiveIndex(Math.min(newActiveIndex, milestones.length - 1));
          
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => window.removeEventListener('scroll', handleScroll);
  }, [milestones.length]);

  // Handle milestone click
  const handleMilestoneClick = (index) => {
    setActiveIndex(index);
  };

  return (
    <div className="beginnings-container" ref={containerRef}>
      <div className="beginnings-content">
        
        {/* Section Header */}
        <div className="beginnings-header">
          <div className="beginnings-badge">Julian's Journey</div>
          <h2 className="beginnings-title">
          From First Ideas to<span className="gradient-text-beginnings"> Real Products.</span>
          </h2>
          <p className="beginnings-subtitle">
            Building scalable platforms that empower communities – from 10,000 musicians to enterprise growth systems
          </p>

          {/* Brag Bar with Logos */}
          <div className="beginnings-brag-bar">
            <h3 className="brag-bar-title">My Own Projects</h3>
            <div className="brag-bar-logos">
              <div className="brag-bar-logo-item">
                <img 
                  src="/img/Democharts_Logo_White.png" 
                  alt="DemoCharts"
                  className="brag-bar-logo"
                />
              </div>
              <div className="brag-bar-logo-item">
                <img 
                  src="/img/wavy_magazine_tablet_logo.png" 
                  alt="Wavy Magazine"
                  className="brag-bar-logo brag-bar-logo-invert"
                />
              </div>
              <div className="brag-bar-logo-item">
                <img 
                  src="/img/bonusclub_logo.webp" 
                  alt="BonusClub"
                  className="brag-bar-logo brag-bar-logo-invert"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Container */}
        <div className="timeline-wrapper">

          {/* Timeline Items */}
          <div className="timeline-items" ref={timelineRef}>
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className={`timeline-item ${index === activeIndex ? 'active' : ''} ${
                  index < activeIndex ? 'passed' : ''
                }`}
                onClick={() => handleMilestoneClick(index)}
                style={{
                  transitionDelay: `${index * 0.1}s`
                }}
              >
                {/* Timeline Dot */}
                <div className="timeline-dot">
                  <div className="timeline-dot-inner">
                    <span className="timeline-year-small">{milestone.month}</span>
                  </div>
                  <div className="timeline-dot-ring"></div>
                  <div className="timeline-dot-pulse"></div>
                </div>

                {/* Content Card */}
                <div className="timeline-card">
                  
                  {/* Images - Overlapping Style */}
                  <div className="timeline-images">
                    <div className="image-stack">
                      <div 
                        className="stacked-image image-back"
                        style={{
                          backgroundImage: `url(${milestone.images[1]})`
                        }}
                      />
                      <div 
                        className="stacked-image image-front"
                        style={{
                          backgroundImage: `url(${milestone.images[0]})`
                        }}
                      />
                    </div>
                    
                    {/* Floating Year Badge */}
                    <div className="floating-year-badge">
                      <span className="year-text">{milestone.year}</span>
                    </div>
                  </div>

                  {/* Text Content */}
                  <div className="timeline-text">
                    <div className="timeline-title-wrapper">
                      <h3 className="timeline-card-title">
                        {milestone.title}
                        {milestone.titleNote && (
                          <span className="timeline-card-title-note"> {milestone.titleNote}</span>
                        )}
                      </h3>
                      {milestone.url && (
                        <a 
                          href={milestone.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="timeline-forward-button"
                          aria-label={`Visit ${milestone.title}`}
                        >
                          <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </a>
                      )}
                    </div>
                    
                    <p className="timeline-card-subtitle">{milestone.subtitle}</p>
                    <p className="timeline-card-description" dangerouslySetInnerHTML={{ __html: milestone.description }}></p>
                    
                    {/* Progress Indicator */}
                    <div className="milestone-progress">
                      <div className="milestone-number">
                        <span>{String(index + 1).padStart(2, '0')}</span>
                        <span className="milestone-total">/{String(milestones.length).padStart(2, '0')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connection Line */}
                {index < milestones.length - 1 && (
                  <div className="timeline-connector">
                    <svg className="connector-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <path
                        d={isMobile ? "M 50 0 L 50 100" : "M 0 50 L 100 50"}
                        className="connector-path"
                        strokeDasharray="5,5"
                      />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Interactive Navigation Dots */}
        <div className="timeline-nav-dots">
          {milestones.map((_, index) => (
            <button
              key={index}
              className={`nav-dot ${index === activeIndex ? 'active' : ''} ${
                index < activeIndex ? 'passed' : ''
              }`}
              onClick={() => handleMilestoneClick(index)}
              aria-label={`Go to milestone ${index + 1}`}
            >
              <span className="nav-dot-year">{milestones[index].year}</span>
            </button>
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <div className="scroll-progress-text">
            {Math.round(scrollProgress * 100)}% read
          </div>
          <div className="scroll-progress-bar">
            <div 
              className="scroll-progress-bar-fill"
              style={{ width: `${scrollProgress * 100}%` }}
            />
          </div>
        </div>

      </div>

      {/* Animated Background Elements */}
      <div className="beginnings-bg-orbs">
        <div className="bg-orb orb-1"></div>
        <div className="bg-orb orb-2"></div>
        <div className="bg-orb orb-3"></div>
      </div>
    </div>
  );
};

export default Beginnings;

